import React, { useState, useEffect } from 'react';
import { fetchExpenses, deleteExpense, fetchProfile as fetchProfileAPI } from '../utils/api';
import { formatDate } from '../utils/date';
import { ADMIN_ROLE, FINANCIAL_STAFF } from '../utils/roles';
import ExpenseCategoryModal from './ExpenseCategoryModal';
import CreateExpenseModal from './CreateExpenseModal';
import './Expenses.css';
import './Invoices.css';
import './_Pagination.css';
import './_Buttons.css';
import './_Input.css';

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [sortBy, setSortBy] = useState('date');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [amount, setAmount] = useState(0);
    const [profile, setProfile] = useState(null);

    const initialFromDate = new Date();
    initialFromDate.setMonth(initialFromDate.getMonth() - 1);
    const fromDateString = initialFromDate.toISOString().split('T')[0];
    const [from, setFrom] = useState(fromDateString);
    const [to, setTo] = useState('');

    const getExpenses = async () => {
        try {
            setLoading(true);
            const response = await fetchExpenses(sortBy, sortDir, page, limit, from, to);
            setExpenses(response.data.expenses || []);
            setTotal(response.data.total || 0);
            setAmount(response.data.amount || 0);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                getExpenses();
            } catch (error) {
                setError(error.message);
            }
        };

        loadData();
    }, [sortBy, sortDir, page, limit, from, to]);

    const handleSort = (newSortBy) => {
        setSortDir(sortBy === newSortBy && sortDir === 'asc' ? 'desc' : 'asc');
        setSortBy(newSortBy);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this expense?')) {
            try {
                await deleteExpense(id);
                getExpenses();
            } catch (error) {
                setError(error.message);
            }
        }
    };

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= Math.ceil(total / limit)) {
            setPage(newPage);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    const totalPages = Math.ceil(total / limit) || 1;

    const canManage = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === FINANCIAL_STAFF);

    return (
        <div className="expenses-container">
            <div className="header">
                <h1>Expenses | {total}</h1>
                <div className="header-controls">
                    <div className="showing-control">
                        <label htmlFor="limit">Showing:</label>
                        <select id="limit" value={limit} onChange={handleLimitChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    {canManage && <button onClick={() => setShowCategoryModal(true)} className="cancel-button text-button">Categories</button>}
                    {canManage && <button onClick={() => setShowCreateModal(true)} className="add-student-button">+</button>}
                </div>
            </div>
            <div className="expenses-filters">
                <div className='expenses-labels'>
                    <label>From: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                    <label>To: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                </div>
                <div className="invoices-total-amount">
                    ${(amount / 100).toFixed(2)}
                </div>
            </div>
            {error && <p className="error">{error}</p>}
            {canManage && <ExpenseCategoryModal show={showCategoryModal} onClose={() => setShowCategoryModal(false)} onCategoryCreated={getExpenses} />}
            {canManage && <CreateExpenseModal show={showCreateModal} onClose={() => setShowCreateModal(false)} onExpenseCreated={getExpenses} />}
            <table className="expenses-table">
                <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th onClick={() => handleSort('date')}>
                            Date {sortBy === 'date' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSort('name')}>
                            Name {sortBy === 'name' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleSort('amount')}>
                            Amount {sortBy === 'amount' && (sortDir === 'asc' ? '▲' : '▼')}
                        </th>
                        <th>Category</th>
                        <th>Description</th>
                        {canManage && <th>Actions</th>}
                    </tr>
                    <tr></tr>
                </thead>
                <tbody>
                    {expenses.map((expense) => (
                        <tr key={expense.id}>
                            <td>{formatDate(expense.date)}</td>
                            <td>{expense.name}</td>
                            <td>${(expense.amount / 100).toFixed(2)}</td>
                            <td>{expense.expense_category.name}</td>
                            <td>{expense.description}</td>
                            {canManage && <td><button onClick={() => handleDelete(expense.id)} className="submit-button text-button">Delete</button></td>}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={canManage ? 6 : 5}>
                            <div className="pagination-controls">
                                <div className="page-buttons">
                                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        {'<'}
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        {'>'}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default Expenses;
